import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { AdminProject } from 'features/projects';
import { Plus, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { useGetRunsQuery } from '../../api/runs';
import { useGetScenariosQuery } from '../../api/scenarios';
import {
  AGENT_EVAL_DEFAULT_MAX_BUDGET_USD,
  AGENT_EVAL_DEFAULT_MAX_SCENARIOS,
  RUNNING_POLL_INTERVAL_MS,
  SCORES_POLL_INTERVAL_MS,
  type AgentTestRun,
} from '../../model/run';
import ScenariosPagination from '../scenarios/ScenariosPagination';
import AutomaticRunsTable from './AutomaticRunsTable';
import ReRunDialog from './ReRunDialog';
import RunFormDialog from './RunFormDialog';
import RunResultsDialog from './RunResultsDialog';
import RunsTable from './RunsTable';

const PAGE_SIZE = 20;

interface RunsSectionProps {
  masterProject: { id: number; name: string };
  subProjects: AdminProject[];
}

const CONFIG_ITEMS: { label: string; value: string }[] = [
  { label: 'Presupuesto máx por run', value: `$${AGENT_EVAL_DEFAULT_MAX_BUDGET_USD},00 USD` },
  { label: 'Tope de escenarios por run', value: String(AGENT_EVAL_DEFAULT_MAX_SCENARIOS) },
  { label: 'Juez', value: 'Evaluadores en Langfuse' },
];

const RunsSection = ({ masterProject, subProjects }: RunsSectionProps): JSX.Element => {
  const [formOpen, setFormOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [resultsRunId, setResultsRunId] = useState<number | null>(null);
  const [reRunTarget, setReRunTarget] = useState<AgentTestRun | null>(null);

  const [pollingInterval, setPollingInterval] = useState(0);
  const { data, isLoading, isFetching, isError, refetch } = useGetRunsQuery(
    { projectId: masterProject.id, page, limit: PAGE_SIZE },
    { pollingInterval }
  );
  const runs = useMemo(() => data?.data ?? [], [data]);
  const pagination = data?.pagination;

  const automaticRuns = useMemo(() => runs.filter((run) => run.triggerType === 'automatic'), [runs]);
  const manualRuns = useMemo(() => runs.filter((run) => run.triggerType !== 'automatic'), [runs]);

  useEffect(() => {
    const hasRunningRun = runs.some((run) => run.status === 'pending' || run.status === 'running');
    const hasPendingScores = runs.some(
      (run) =>
        (run.status === 'completed' || run.status === 'partial' || run.status === 'aborted') &&
        (run.scoresStatus === 'pending' || run.scoresStatus === 'partial')
    );
    setPollingInterval(hasRunningRun ? RUNNING_POLL_INTERVAL_MS : hasPendingScores ? SCORES_POLL_INTERVAL_MS : 0);
  }, [runs]);

  const { data: scenariosData } = useGetScenariosQuery({ projectId: masterProject.id, page: 1, limit: 100 });
  const scenarios = useMemo(() => scenariosData?.data ?? [], [scenariosData]);

  const scenarioNames = useMemo(() => {
    const names: Record<number, string> = {};
    for (const scenario of scenarios) {
      names[scenario.id] = scenario.name;
    }
    return names;
  }, [scenarios]);

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4 pb-10">
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border-subtle
            bg-bg-surface-raised px-4 py-2.5"
        >
          <ShieldCheck className="size-4 shrink-0 text-text-muted" aria-hidden />
          {CONFIG_ITEMS.map((item) => (
            <span key={item.label} className="flex items-center gap-1.5 text-xs">
              <span className="text-text-muted">{item.label}:</span>
              <span className="font-semibold text-text-default">{item.value}</span>
            </span>
          ))}
        </div>
        <Button type="button" onClick={() => setFormOpen(true)}>
          <Plus className="size-4" aria-hidden />
          Nuevo run
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-xl border border-border-default bg-bg-surface-alt py-14">
          <p className="m-0 text-sm text-text-muted font-body">Cargando runs…</p>
        </div>
      ) : null}

      {isError && !isLoading ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-xl border border-error/30 bg-error-light px-6 py-10 text-center"
        >
          <p className="m-0 text-sm text-error font-body">No se pudieron cargar los runs.</p>
          <Button type="button" variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && runs.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border-default
            bg-bg-surface-alt px-6 py-14 text-center"
          role="status"
        >
          <p className="m-0 max-w-md text-sm text-text-muted font-body">
            Aún no hay runs para esta inmobiliaria. Crea el primero para testear el agente.
          </p>
          <Button type="button" onClick={() => setFormOpen(true)}>
            Nuevo run
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && runs.length > 0 ? (
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          <div className="flex flex-col gap-2">
            <p className="m-0 text-sm font-semibold text-text-default">Runs automáticos</p>
            {automaticRuns.length > 0 ? (
              <AutomaticRunsTable runs={automaticRuns} onViewResults={(run) => setResultsRunId(run.id)} />
            ) : (
              <p className="m-0 text-xs text-text-muted">Aún no hay runs automáticos para esta inmobiliaria.</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <p className="m-0 text-sm font-semibold text-text-default">Runs manuales</p>
            {manualRuns.length > 0 ? (
              <RunsTable runs={manualRuns} onViewResults={(run) => setResultsRunId(run.id)} onReRun={setReRunTarget} />
            ) : (
              <p className="m-0 text-xs text-text-muted">Aún no hay runs manuales para esta inmobiliaria.</p>
            )}
          </div>

          {pagination && pagination.totalPages > 1 ? (
            <div className="shrink-0">
              <ScenariosPagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                isFetching={isFetching}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <RunFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        masterProject={masterProject}
        subProjects={subProjects}
        scenarios={scenarios}
      />

      <RunResultsDialog
        runId={resultsRunId}
        scenarioNames={scenarioNames}
        onRunChange={setResultsRunId}
        onOpenChange={(open) => {
          if (!open) setResultsRunId(null);
        }}
      />

      <ReRunDialog
        run={reRunTarget}
        open={reRunTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReRunTarget(null);
        }}
      />
    </section>
  );
};

export default RunsSection;
